import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import {useData} from '../../context/DataContext';
import {useLang} from '../../context/LanguageContext';
import { FaArrowLeft } from 'react-icons/fa';
import TitleOneCard from '../../components/cardsGeneration/titleOneCard';
import {useAuth} from "../../context/AuthContext";
import DateUtils from "../../utils/dateUtils";
import Footer from "../../components/Footer";


const GenerateSmallOddsPage = () => {
  const navigate = useNavigate();
  const { games } = useData();
  const { t } = useLang();
  const { currentUser } = useAuth();
  const user = currentUser;

  const [selectedDays, setSelectedDays] = useState([]);
  const [numberOfGames, setNumberOfGames] = useState('');
  const [error, setError] = useState(null);

  const allDays = Array.from(new Set(games?.map((g) => g.day))) || [];

  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter((d) => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleGenerate = async () => {
    setError(null);

    // 1) If not logged in, redirect
    if (!user) {
      navigate('/account');
      return;
    }

    // 2) Validate
    const num = parseInt(numberOfGames, 10);
    if (selectedDays.length === 0) {
      setError(t('error.atLeastADay'));
      return;
    }
    if (!num || num <= 0) {
      setError(t('error.selectValidNumberOfGames'));
      return;
    }

    // 3) Filter the games to selected days
    const filteredGames = games.filter((g) => selectedDays.includes(g.day));
    if (filteredGames.length < num) {
      setError(t('error.notEnoughGames'));
      return;
    }

    // 4) Random unique selection
    const uniqueGames = getRandomUniqueGames(filteredGames, num);
    if (uniqueGames.length < num) {
      setError(t('error.notEnoughUniqueGames'));
      return;
    }

    // 5) Calculate totalOdd
    let totalOdd = 1;
    uniqueGames.forEach((gm) => {
      const oddVal = parseFloat(gm.odd);
      if (!isNaN(oddVal)) {
        totalOdd *= oddVal;
      }
    });

    // 6) Write doc to "generateSmallOdds"
    const docData = {
      dateCreated: Date.now(),
      info: {
        appVersion: '3.3.4',
        device: 'android',
      },
      meciuri: uniqueGames.map((gm) => gm.id),
      status: 'active',
      totalGames: uniqueGames.length,
      totalOdd: totalOdd,
      userId: user.uid,
    };

    try {
      await addDoc(collection(db, 'generated'), docData);
    } catch (err) {
      console.error('Error writing ticket:', err);
      setError('Could not save ticket. Try again later.');
      return;
    }

    // 7) Also store in localStorage for the GeneratedTicketPage
    localStorage.setItem('generatedGames', JSON.stringify(uniqueGames));

    // 8) Navigate to generated ticket page
    navigate('/generated-ticket');
  };

  return (
    <div className="bg-[#34495e] min-h-screen p-5 relative">
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
      >
        <FaArrowLeft />
        <span className="text-white">{t('btn.back')}</span>
      </button>

      <h1 className="text-xl text-white font-bold text-center mt-16 mb-8">
        {t('txt.generateSmallOdds')}
      </h1>

      <div className="mb-4">
        <TitleOneCard title={t('txt.generateCard1')} />
        <p className="text-white mb-2 font-semibold">{t('txt.selectDays')}</p>
        <div className="flex flex-wrap gap-2">
          {allDays.map((day) => {
            const translatedDayLabel = DateUtils.formatDayOnly(day, t);

            return (
              <button
                key={day}
                onClick={() => toggleDay(day)}
                className={`px-3 py-1 rounded ${
                  selectedDays.includes(day) 
                    ? 'bg-green-500 text-white' 
                    : 'bg-gray-200'
                }`}
              >
                {translatedDayLabel}
              </button>
            );
          })}
        </div>
      </div>

      {/* Number of games input */}
      <div className="mb-4">
        <p className="text-white mb-2 font-semibold">{t('txt.selectNumberOfGames')}</p>
        <input
          type="number"
          className=" w-full border p-2 rounded w-32"
          value={numberOfGames}
          onChange={(e) => setNumberOfGames(e.target.value)}
        />
      </div>

      {error && <p className="text-red-500 mb-2">{error}</p>}

      <button
        onClick={handleGenerate}
        className="bg-blue-500 text-white font-bold py-2 px-4 rounded hover:bg-blue-600 transition"
      >
        {t('btn.generate')}
      </button>

        <TitleOneCard title={t('txt.generateCard2')} />
        <TitleOneCard title={t('txt.generateCard3')} />
      <Footer />
    </div>
  );
};

function getRandomUniqueGames(array, count) {
  const shuffled = [...array];
  // Shuffle
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  const selected = [];
  const usedTeams = new Set();
  const usedShopCodes = new Set();

  for (let item of shuffled) {
    // Check duplicates
    if (usedShopCodes.has(item.shopCode)) continue;
    if (usedTeams.has(item.team1) || usedTeams.has(item.team2)) continue;

    // Accept this item
    selected.push(item);
    usedShopCodes.add(item.shopCode);
    usedTeams.add(item.team1);
    usedTeams.add(item.team2);

    if (selected.length === count) break;
  }
  return selected;
}

export default GenerateSmallOddsPage;
